import '../style/chat.scss';
import './viewport.scss';
import {Suspense, lazy} from "preact/compat";
import {FunctionalComponent, h, Fragment, ComponentChild} from 'preact';
import TextBox from "../chat-msg-elements/text-box/TextBox";
import InputUI from '../chat-input/inputUI';
import {ChatServerMsg, TYPES} from '../libs/config';
import {MessageBoxTemplate} from "../chat-msg-elements/MessageBoxTemplate";
import Typing from "../chat-msg-elements/typing-effect/typing";
import MediaBox from "../chat-msg-elements/media-box/MediaBox";
import {ScrollButt} from "./scrollButt";
import {useEffect, useRef, useState} from "preact/hooks";
import {EmbedBox} from "../chat-msg-elements/embed-box/EmbedBox";
import {PayBox} from "../chat-msg-elements/pay-box/PayBox";
import {throttle, typeToHR} from "../libs/ScriptHelper";
import {EFFECTS} from "../effects/effectsContext";
import QuestionBox from "../chat-msg-elements/question-box/QuestionBox";
import {Loader} from "../components/loader";
import ChangeBackground from "../chat-msg-elements/change-background/ChangeBackground";
import {log as logger} from "../libs/QuerloLogger";
import ClearChat from "../chat-msg-elements/clear-chat/ClearChat";
import SetInputUISticky from "../chat-msg-elements/set-input-ui-sticky/SetInputUISticky";
import HumanTakeover from "../chat-msg-elements/human-takeover/HumanTakeover";
import SetGlobalCfgItem, {ConfigItem} from "../chat-msg-elements/set-global-cfg-item/SetGlobalCfgItem";
import SystemMessage from "../chat-msg-elements/system-message/SystemMessage";
import FileAttachment from "../chat-msg-elements/file-attachment/FileAttachment";

const Cards = lazy(() => import('../chat-msg-elements/cards/Cards'));

const ChatViewport: FunctionalComponent<{
    messages: ChatServerMsg[];
    chatID: number;
    assetsBaseURL: string;
    backendBaseURL: string;
    voiceInputEnabled: boolean;
    payCallback: (replyToServerMsg: any) => void;
    payPalId: string;
    changeBackgroundCallback: (bkgData: any) => void;
    clearChatCallback: () => void;
    lightboxCallback: (content: ComponentChild) => void;
    setGlobalCfgItemCallback: (cfgItemData: ConfigItem) => void;
    answerCallback: (replyToServerMsg: any) => void;
    tickCallback: () => void;
    isTypingEnabled: boolean;
    messageEnterClass: string;
}> =
    ({
         messages,
          chatID,
         payCallback,
         payPalId,
         changeBackgroundCallback,
         clearChatCallback,
         lightboxCallback,
        setGlobalCfgItemCallback,
         answerCallback,
         assetsBaseURL,
         backendBaseURL,
         voiceInputEnabled,
         tickCallback,
        isTypingEnabled,
        messageEnterClass
     }) => {

        logger.debug(`@@_VP_@@ Render, last msg: ${messages[messages.length - 1] ? (messages[messages.length - 1].id + ' t: ' + typeToHR(messages[messages.length - 1].t)) : 'n/a'}`);

        const chatViewportWrpEl = useRef<HTMLDivElement>(null);
        const viewportEl = useRef<HTMLUListElement>(null);
        const viewportWrpEl = useRef<HTMLDivElement>(null);
        const inputUIPreviousHeight = useRef<number>(0);

        const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
        const [activeQuestion, setActiveQuestion] = useState<any>(null);
        const [stickyInputUI, setStickyInputUI] = useState<boolean>(false);
        const [inputUISlideState, setInputUISlideState] = useState<string>('IN');
        // const [viewPortSpacer, setViewPortSpacer] = useState<number>(0);

        /** needs to be called whenever view port height changes b/c a new message has been added or changed its size,
         * for example when a media box finishes loading its assets and renders them in place of the loading animation
         */
        function scrollViewPortToBottom(): void {
          if (!viewportWrpEl.current || !viewportEl.current) {
            return;
          }
          /** scroll to bottom */
             viewportWrpEl.current.scroll({ top: viewportEl.current.scrollHeight, left: 0, behavior: 'smooth' });
             // viewportWrpEl.current.scrollTop = viewportEl.current.scrollHeight;
        }

      function showInputBox(questionRawMsg: any): void {
        logger.debug(`@@_VP_@@ showInputBox called for question ${questionRawMsg.question} [${questionRawMsg.questionType}]`);
          setActiveQuestion(questionRawMsg);
        setInputUISlideState('IN');
      }

      function hideInputBox(): void {
        setInputUISlideState('OUT');
      }

      function setInputUIStickyCallback(stickyData: any): void {
          if (stickyData.v) {
            setStickyInputUI(true);
            // eslint-disable-next-line @typescript-eslint/camelcase
            showInputBox({questionType: 1000, dataType: "", chat_id: chatID});
          } else {
            setStickyInputUI(false);
            hideInputBox();
          }
        }

        function messageRenderedCallBack(h: number): void {
          if (!viewportWrpEl.current) {
            return;
          }
          const viewportPaddingBottom = parseInt(viewportWrpEl.current.style.paddingBottom.replace('px', ''), 10) || 0;
            if (viewportPaddingBottom &&
                (!stickyInputUI || (inputUIPreviousHeight.current < viewportPaddingBottom)) &&
                messages[messages.length - 1] &&
                ( !isTypingEnabled || /* if there are no typing messages, every kind of message must be used to decrease padding */
                (messages[messages.length - 1].t === TYPES.USER_TYPING || messages[messages.length - 1].t === TYPES.POLL_ANSWER)
                )
            ) {
                logger.debug(`@@_VP_@@ MSG RENDERED ${messages[messages.length - 1] ? messages[messages.length - 1].id : 'n/a'}, vp padding-bottom decreased from ${viewportPaddingBottom}px to ${Math.max(Math.max(0, inputUIPreviousHeight.current), viewportPaddingBottom - h)}px, msg height: ${h}px`);
                viewportWrpEl.current.style.paddingBottom = `${Math.max(Math.max(0, inputUIPreviousHeight.current), viewportPaddingBottom - h).toString()  }px`;
            } else {
                logger.debug(`@@_VP_@@ MSG RENDERED ${messages[messages.length - 1] ? messages[messages.length - 1].id : 'n/a'}, inputUI height: ${inputUIPreviousHeight.current}px, vp padding-bottom (${viewportPaddingBottom}px) left unchanged, msg height: ${h}px`);
            }
            // setViewPortSpacer(Math.max(0, viewPortSpacer - h));
            scrollViewPortToBottom();
        }

      /**
       * called from useEffect inside InputUI component when it's rendered
       *
       * @param inputUIHeight
       */
        function bumpViewportContentUp(inputUIHeight: number): void {
          if (!viewportWrpEl.current || !viewportEl.current) {
            return;
          }
          const prevInputUIHeight = inputUIPreviousHeight.current;
          inputUIPreviousHeight.current = inputUIHeight;

        /** this can happen, for example, when there is a sticky InputUI with buttons and then buttons disappear, leaving only the input box.
         * In such a case, if we suddenly decrease the padding to the new, lower, height, messages will jump down suddenly, while we
         * want the padding to be kept and gradually removed when new messages arrive
         */
        if (inputUIHeight <= prevInputUIHeight) {
            logger.debug(`@@_VP_@@ slideUp, bailing out set padding b/c InputUI size has not increased (${prevInputUIHeight}px -> ${inputUIHeight}px - vpWrp scrTop: ${viewportWrpEl.current.scrollTop}, vp scrH: ${viewportEl.current.scrollHeight}, last msg: ${messages[messages.length - 1] ? messages[messages.length - 1].id : 'n/a'},  vp OffH: ${viewportEl.current.offsetHeight}, vpWrp OffH: ${viewportWrpEl.current.offsetHeight}, InputUI H: ${inputUIHeight}`);
            return;
          }

            /**
             *  consider that in case this is called when there is still a padding-bottom set, it could even be set to a lower value
             *  and maybe cause some glitches?
             *  TODO to be checked and fixed, probably by using a Math.max between current padding-bottom and the one desired
             *  */
            if (stickyInputUI || viewportEl.current.offsetHeight >= viewportWrpEl.current.offsetHeight - inputUIHeight) {
                viewportWrpEl.current.style.paddingBottom = `${Math.abs(inputUIHeight)}px`;
              logger.debug(`@@_VP_@@ slideUp, vp padding set to: ${Math.abs(inputUIHeight)}px - vpWrp scrTop: ${viewportWrpEl.current.scrollTop}, vp scrH: ${viewportEl.current.scrollHeight}, last msg: ${messages[messages.length - 1] ? messages[messages.length - 1].id : 'n/a'},  vp OffH: ${viewportEl.current.offsetHeight}, vpWrp OffH: ${viewportWrpEl.current.offsetHeight}, InputUI H: ${inputUIHeight}`);
              scrollViewPortToBottom();
            } else {
              logger.debug(`@@_VP_@@ slideUp, bailing out set padding b/c messages haven't reached viewport bottom yet - vpWrp scrTop: ${viewportWrpEl.current.scrollTop}, vp scrH: ${viewportEl.current.scrollHeight}, last msg: ${messages[messages.length - 1] ? messages[messages.length - 1].id : 'n/a'},  vp OffH: ${viewportEl.current.offsetHeight}, vpWrp OffH: ${viewportWrpEl.current.offsetHeight}, InputUI H: ${inputUIHeight}`);
            }
        }

        function scrollHandler(): void {
          if (!viewportWrpEl.current) {
            return;
          }
          const offs = (viewportWrpEl.current.scrollHeight - viewportWrpEl.current.offsetHeight) - viewportWrpEl.current.scrollTop;
          setShowScrollButton(offs >= 96);
        }

        function resizeHandler(): void {
          scrollViewPortToBottom();
        }

        /** called when the slide down animation of input box has completed, to destroy inputUI component */
        function inputHiddenCB(): void {
            setActiveQuestion(null);
            inputUIPreviousHeight.current = 0;
        }

        function answerCB(replyToServerMsg: any): void {
          if (!activeQuestion || !stickyInputUI) {
            hideInputBox();
          } else if (stickyInputUI) {
            // here we are just changing the active question back to the dummy type 1000 question.
            // eslint-disable-next-line @typescript-eslint/camelcase
            setActiveQuestion({questionType: 1000, dataType: "", chat_id: chatID});
          }
            answerCallback(replyToServerMsg);
        }

        /** run only once after HTML has been rendered */
        useEffect(() => {
          if (!viewportWrpEl.current) {
            return;
          }
          viewportWrpEl.current.addEventListener('scroll', throttle(scrollHandler, 100));
          window.addEventListener('resize', throttle(resizeHandler, 100));
        }, []);

        /** run, after component render, everytime a new message is added to messages array to be shown
         *  and we must scroll down the viewport to bring the new message into view.
         *  Also used to hide inputBox when no longer needed.
         * */
        useEffect(() => {
          logger.debug(
            `@@_VP_@@ useEffect, activeQuestion: ${activeQuestion ? (`${activeQuestion.question ?? ""} T: ${activeQuestion.questionType}`) : JSON.stringify(activeQuestion)}, last msg: ${
              messages[messages.length - 1]
                ? messages[messages.length - 1].id +
                  " t: " +
                  typeToHR(messages[messages.length - 1].t)
                : "n/a"
            }`
          );

          /** needed, for example, when jumping to another chat via API when chat was waiting for an answer
           * In particular, messages.length === 0 is needed in case we receive the CLEAR-CHAT event when InputBox is shown */
          if (
            activeQuestion &&
            !stickyInputUI &&
            (messages.length === 0 ||
              messages[messages.length - 1].t !== TYPES.SEND_POLL ||
              messages[messages.length - 1].b.questionType === 8)
          ) {
            hideInputBox();
          }
        }, [messages[messages.length - 1]]);

        function renderMessage(rawMsg: ChatServerMsg): ComponentChild {
            let content: ComponentChild;
            switch (rawMsg.t) {
                case TYPES.HUMAN_TAKEOVER:
                  content =
                    <HumanTakeover msgID={rawMsg.id}
                             name={rawMsg.b.name}
                                   action={rawMsg.b.action}
                             time={rawMsg.time}
                             tickCallback={tickCallback} />;
                  break;
                case TYPES.SYSTEM_MESSAGE:
                  content =
                    <SystemMessage msgID={rawMsg.id}
                             msgText={rawMsg.b.txt}
                             time={rawMsg.time}
                             tickCallback={tickCallback} />;
                  break;
              case TYPES.CHAT_IMG_ATTACHMENT:
                content = <FileAttachment msgID={rawMsg.id}
                                  fileName={rawMsg.b.f}
                                  mimeType={rawMsg.b.mt}
                                  assetsBaseURL={assetsBaseURL}
                                  lightboxCallback={lightboxCallback}
                                  time={rawMsg.time}
                                  tickCallback={tickCallback} />;
                break;
                case TYPES.CHAT_TXT_MSG:
                case TYPES.GENERIC_ERROR:
                    //TODO if b.txt === '' b is picked and [Object] is printed into the bubble
                    content =
                        <TextBox msgID={rawMsg.id}
                                 name={rawMsg.name}
                                 time={rawMsg.time}
                                 msgText={rawMsg.b.txt || rawMsg.b}
                                 tickCallback={tickCallback} />;
                    break;
                case TYPES.POLL_ANSWER:
                    //TODO if b.txt === '' b is picked and [Object] is printed into the bubble
                    if (!rawMsg.b.txt && typeof rawMsg.b !== 'string') {
                      tickCallback();
                      return null;
                    }
                    content =
                        <TextBox disableEffects={[EFFECTS.SPEAK]}
                                 name={rawMsg.name}
                                 time={rawMsg.time}
                                 msgID={rawMsg.id}
                                 msgText={rawMsg.b.txt || rawMsg.b}
                                 tickCallback={tickCallback} />;
                    break;
                case TYPES.SEND_POLL:
                    if (rawMsg.b.questionType === 8) {
                      content = <Suspense fallback={<Loader />}>
                                  <Cards msgID={rawMsg.id}
                                         tickCallback={tickCallback}
                                         msgRenderedCb={messageRenderedCallBack}
                                         questionRawObject={rawMsg.b}
                                         cardsCB={answerCB} />
                                </Suspense>
                    } else {
                      content = <QuestionBox questionRawObject={rawMsg.b}
                                             msgID={rawMsg.id}
                                             msgText={rawMsg.b.question}
                                             showInputBoxCallback={showInputBox}
                                             tickCallback={tickCallback} />;
                    }
                    break;
                case TYPES.VIDEO_MSG:
                    content = <EmbedBox msgID={rawMsg.id}
                                        videoURL={rawMsg.b.video_url}
                                        tickCallback={tickCallback} />;
                    break;
                case TYPES.POLL_IMG:
                    content = <MediaBox msgID={rawMsg.id}
                                        extLink={rawMsg.b.href}
                                        assetsBaseURL={assetsBaseURL}
                                        metaData={rawMsg.b.metadata}
                                        rawImageObj={rawMsg.b}
                                        desc={rawMsg.b.desc}
                                        lightboxCallback={lightboxCallback}
                                        updateViewportCallback={scrollViewPortToBottom}
                                        tickCallback={tickCallback} />;
                    break;
                case TYPES.PAY_BUTTON:
                    content = <PayBox key={rawMsg.id}
                                      msgID={rawMsg.id}
                                      payPalID={payPalId}
                                      payButtonCfg={rawMsg.b}
                                      payCallback={payCallback} />;
                    break;
                case TYPES.USER_TYPING:
                  content = <Typing key={rawMsg.id} msgID={rawMsg.id} duration={rawMsg.b.delay} tickCallback={tickCallback} />;
                    break;
                case TYPES.CMD_CHANGE_BACKGROUND:
                    // changeBackgroundCallback(rawMsg.b);
                    return <ChangeBackground msgID={rawMsg.id} backgroundData={rawMsg.b}
                                             changeBackgroundCallback={changeBackgroundCallback}
                                             tickCallback={tickCallback} />
                case TYPES.CMD_CLEAR_CHAT:
                    return <ClearChat msgID={rawMsg.id}
                                             clearChatCallback={clearChatCallback}
                                             tickCallback={tickCallback} />
                case TYPES.CMD_SET_INPUT_UI_STICKY:
                    return <SetInputUISticky msgID={rawMsg.id}
                                              stickyData={rawMsg.b}
                                             setInputUIStickyCallback={setInputUIStickyCallback}
                                             tickCallback={tickCallback} />
                case TYPES.CMD_SET_GLOBAL_CFG_ITEM:
                    return <SetGlobalCfgItem msgID={rawMsg.id}
                                              cfgItemData={rawMsg.b}
                                             setGlobalCfgItem={setGlobalCfgItemCallback}
                                             tickCallback={tickCallback} />
                default:
                    content = <TextBox msgID={rawMsg.id}
                                       name={rawMsg.name}
                                       time={rawMsg.time}
                                       msgText={`unsupported message type: ${rawMsg.t}, '${JSON.stringify(rawMsg)}'`}
                                       tickCallback={tickCallback} />;
                    //throw('Unsupported message type');
                    break;
            }
            return (<MessageBoxTemplate avt1={rawMsg.avatar1}
                                        avt2={rawMsg.avatar2}
                                        key={rawMsg.id}
                                        me={rawMsg.me}
                                        typing={rawMsg.t === TYPES.USER_TYPING}
                                        merged={!!rawMsg.merged}
                                        msgRenderedCb={messageRenderedCallBack}>
                {content}
            </MessageBoxTemplate>);
        }

        const log = messages.map(
            (c: ChatServerMsg) => renderMessage(c)
        );
        /*            const lastMess = messages[len - 1];

                    if (lastMess.t === TYPES.SEND_POLL) {
                        question = lastMess.b;
                        /!** MULTIPLE CHOICE QUESTION *!/
                        if (lastMess.b.questionType === 0) {
                        }
                    } else {
                    }*/

        return (
          <Fragment>
              <div id="chatViewportWrp" ref={chatViewportWrpEl}>
                <div ref={viewportWrpEl}
                     className="viewportWrp scrollWrp"
                     aria-live="assertive"
                     role="log">
                    <ul ref={viewportEl}
                        className={`chatViewport${messageEnterClass ?  ` ${messageEnterClass}` : ''}`}
                        role="list">
                        {log}
                    </ul>
                </div>
                {showScrollButton && (<ScrollButt scrollButtonCB={scrollViewPortToBottom}
                                                  marginBottom={viewportWrpEl.current ? viewportWrpEl.current.style.paddingBottom : '0px'} />)}
              </div>
              {activeQuestion !== null && <InputUI slideState={inputUISlideState}
                    backendBaseURL={backendBaseURL}
                    voiceInputEnabled={voiceInputEnabled}
                    question={activeQuestion}
                    renderedCallback={bumpViewportContentUp}
                    answerCallback={answerCB}
                    hiddenCallback={inputHiddenCB}
                    sticky={stickyInputUI}
              />}
          </Fragment>
        );

    }

export default ChatViewport;
